import { useCallback, useMemo } from "react";
import { APP_ROUTES, ROUTE_PATHS } from "../../constants/app.constants";
import styles from "./index.module.scss";
import { Loaders } from "../../common/store/loaders/loaders.enum";
import { useLocation, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import cn from "../../Utils/class-names.utils";
import CompanySettings from "../distributor-settings/company-settings";
import InfoBanner from "./info-banner";
import TechnologySpendSection from "../settingsV2/technology-spend/technology-spend.section";
import SupplierAndManufacturers from "../settingsV2/supplierAndManufacturers";
import MembersAccessPage from "../hg-settings/members-access";
import MSPClientAccessLevels from "../MSPSettingsv2/mspClientAccessLevel/MSPClientAccessLevels";
import ActionCentreV2 from "../settingsV2/action-center-notification/Index";
import RecalculateScores from "../settingsV2/recalculate-scores";
import useQueryParams from "../../hooks/query-params.hook";

const TabKeys = {
  companySettings: "companySettings",
  categoryManagement: "categoryManagement",
  supplierAndManufacturers: "supplierAndManufacturers",
  membersAndAccess: "membersAndAccess",
  clientAccessRoles: "clientAccessRoles",
  actionCentre: "actionCentre",
  recalculateScores: "recalculateScores",
}

const MspSettings = ({ accountId, distributorId, isDistributorView = false }) => {
  const { getParam } = useQueryParams();
  const variant = getParam('variant') || 'v1';

  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = useMemo(() => {
    const currentPath = location.pathname;
    const routes = isDistributorView 
      ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages
      : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages;

    for (const [tabKey, routeTemplate] of Object.entries(routes)) {
      if (typeof routeTemplate !== "string") continue;
      
      // Build full path for comparison
      const fullPath = isDistributorView
        ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages[tabKey](distributorId, accountId)
        : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages[tabKey](accountId);
      
      // Check if current path matches or starts with the route path
      if (currentPath === fullPath || currentPath.startsWith(fullPath + '/')) {
        return tabKey;
      }
    }
    
    return TabKeys.companySettings;
  }, [location.pathname, accountId, distributorId, isDistributorView]);

  const sidebarItems = useMemo(
    () => [
      {
        tabName: "Company Settings",
        tabImage: "/images/settings-nav/company_settings.svg",
        tabURL: isDistributorView
          ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.companySettings(distributorId, accountId)
          : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.companySettings(accountId),
        loader: Loaders.GetCompanySettingsLoader,
        isActive: activeTab === TabKeys.companySettings,
        isCentered: true,
      },
      {
        tabName: "Category Management",
        tabImage: "/images/settings-nav/categories-management.svg",
        tabURL: isDistributorView
          ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.categoryManagement(distributorId, accountId)
          : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.categoryManagement(accountId),
        loader: Loaders.GetCategoriesLoader,
        isActive: activeTab === TabKeys.categoryManagement,
        isCentered: true,
      },
      {
        tabName: "Supplier & Manufacturers",
        tabImage: "/images/settings-nav/supplier_and_manufacturers.svg",
        tabURL: isDistributorView
          ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.supplierAndManufacturers(distributorId, accountId)
          : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.supplierAndManufacturers(accountId),
        loader: Loaders?.GetSupplierAndManufacturersLoader,
        isActive: activeTab === TabKeys.supplierAndManufacturers,
        isCentered: false,
      },
      {
        tabName: "Members and access",
        tabImage: "/images/settings-nav/members.svg",
        tabURL: isDistributorView
          ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.membersAndAccess(distributorId, accountId)
          : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.membersAndAccess(accountId),
        loader: [
          Loaders.GetMemberDetailsLoader,
          Loaders.GetAccessRolesDetailsLoader,
        ],
        isActive: activeTab === TabKeys.membersAndAccess,
        isCentered: true,
      },
      {
        tabName: "Client access roles",
        tabImage: "/images/settings-nav/company_settings.svg",
        tabURL: isDistributorView
          ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.clientAccessRoles(distributorId, accountId)
          : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.clientAccessRoles(accountId),
        loader: Loaders.GetClientAccessRolesDetailsLoader,
        isActive: activeTab === TabKeys.clientAccessRoles,
        isCentered: true,
      },
      {
        tabName: "Action Centre",
        tabImage: "/images/settings-nav/action_centre.svg",
        tabURL: isDistributorView
          ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.actionCentre(distributorId, accountId)
          : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.actionCentre(accountId),
        isActive: activeTab === TabKeys.actionCentre,
        isCentered: true,
      },
      {
        tabName: "Recalculate Scores",
        tabImage: "/images/settings-nav/active_sessions.svg",
        tabURL: isDistributorView
          ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.recalculateScores(distributorId, accountId)
          : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.recalculateScores(accountId),
        isActive: activeTab === TabKeys.recalculateScores,
        isCentered: true,
      },
    ],
    [accountId, distributorId, activeTab, isDistributorView]
  );

  const activeItem = useMemo(() => {
    return sidebarItems.find((item) => item.isActive) || sidebarItems[0];
  }, [sidebarItems]);

  const getRoutePath = useCallback((routeKey) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('variant', variant);
    if (isDistributorView) {
      return `${APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages[routeKey]
        .replace(':distributorId', distributorId)
        .replace(':mspAccountId', accountId)}?${queryParams.toString()}`;
    }
    return `${APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages[routeKey].replace(':accountId', accountId)}?${queryParams.toString()}`;
  }, [accountId, distributorId, isDistributorView, variant]);

  const isView = true;

  return (
    <div className={styles.page}>
      <div className={styles.sidebar}>
        {sidebarItems.map((item) => (
          <div className={cn(styles.sidebarItem, {
            [styles.active]: item.isActive,
          })} key={item.tabName} onClick={() => {
            const queryParams = new URLSearchParams(location.search);
            queryParams.set('variant', variant);
            navigate(`${item.tabURL}?${queryParams.toString()}`);
          }}>
            <div className={styles.sidebarIconWrapper}>
              <img src={item.tabImage} alt={""} className={styles.sidebarIcon} />
            </div>
            <span className={styles.sidebarText}>{item.tabName}</span>
          </div>
        ))}
      </div>
      <div className="d-flex flex-column w-100">
        {isView && (
          <div className={cn(styles.bannerWrapper, {
            [styles.centered]: activeItem.isCentered,
          })}>
            <InfoBanner />
          </div>
        )}
        <Routes>
          <Route 
            path={isDistributorView 
              ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.companySettings
              : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.companySettings
            } 
            element={<CompanySettings isView={isView} isDistributorView={isDistributorView} />}
          />
          <Route 
            path={isDistributorView 
              ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.categoryManagement
              : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.categoryManagement
            } 
            element={<TechnologySpendSection isView={isView} />} 
          />
          <Route 
            path={isDistributorView 
              ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.supplierAndManufacturers
              : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.supplierAndManufacturers
            } 
            element={<SupplierAndManufacturers isView={isView} />} 
          />
          <Route 
            path={isDistributorView 
              ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.membersAndAccess
              : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.membersAndAccess
            } 
            element={<MembersAccessPage isView={isView} />} 
          />
          <Route 
            path={isDistributorView 
              ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.clientAccessRoles
              : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.clientAccessRoles
            } 
            element={<MSPClientAccessLevels isView={isView} />} 
          />
          <Route 
            path={isDistributorView 
              ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.actionCentre
              : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.actionCentre
            } 
            element={<ActionCentreV2 isView={isView} />} 
          />
          <Route 
            path={isDistributorView 
              ? APP_ROUTES.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.recalculateScores
              : APP_ROUTES.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.recalculateScores
            } 
            element={<RecalculateScores isView={isView} />} 
          />
          <Route path="" element={<Navigate to={isDistributorView ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.companySettings(distributorId, accountId) : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.companySettings(accountId)} replace />} />
          <Route path="*" element={<Navigate to={isDistributorView ? ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountDetailsPages.settingsPages.companySettings(distributorId, accountId) : ROUTE_PATHS.subscriptionPortalPages.mspAccountDetailsPages.settingsPages.companySettings(accountId)} replace />} />
        </Routes>
      </div>
    </div>
  )
}

export default MspSettings;
